import { useCallback } from "react";
import { useSelector } from "react-redux";
import { getProgram, isItemExcludedFromRedemption } from "../program";
import { selectActiveLoyaltyProvider, selectLoyaltyProgramDetails } from "../selectors";
import type { ExcludableItem } from "../types";

/**
 * Whether points may be spent on a given cart line.
 *
 * ```ts
 * const isExcludedForLoyalty = useIsItemExcludedFromRedemption();
 * isExcludedForLoyalty(saleItem) && <Text>{ITEM_NOT_ELIGIBLE_FOR_REDEMPTION}</Text>
 * ```
 *
 * A predicate rather than a boolean because a cart renders one line per item and each asks about
 * its own; returning a callback keeps the two store reads to one subscription per cart line
 * instead of one per read, and keeps the provider pairing out of the view. Which providers have
 * exclusions at all, and how an exclusion matches, are `isItemExcludedFromRedemption`'s business.
 *
 * Stable across renders while the programme and the active provider are unchanged, so it is safe
 * in a dependency array.
 */
export function useIsItemExcludedFromRedemption() {
  const programDetails = useSelector(selectLoyaltyProgramDetails);
  const activeProvider = useSelector(selectActiveLoyaltyProvider);

  const program = getProgram(programDetails);
  const providerMetaId = activeProvider?.metaId;

  return useCallback(
    (item: ExcludableItem | null | undefined) =>
      isItemExcludedFromRedemption(item, program, providerMetaId),
    [program, providerMetaId],
  );
}
